import {
  ApiError,
  createClubCoreClient,
  MemoryTokenStore,
} from "@clubcore/api";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useMe } from "../../app/auth";
import { useClub } from "../../app/club";
import { useApi } from "../../app/services";
import { useT } from "../../i18n/I18nProvider";
import { Button } from "../../ui/Button";
import { TextField } from "../../ui/Field";
import { useErrorMessage } from "../../ui/feedback";
import { Modal } from "../../ui/Modal";
import { QrExit } from "./QrExit";
import "./operations/messages";

export const isPosWorkspace = (path: string) =>
  /^\/pos\/(checks|kitchen|cash|payment-recovery|settled-refunds)(\/|$)/.test(path);

/** In-app confinement only. OS kiosk mode is required to restrict the whole device. */
export function usePosMode() {
  const { club } = useClub();
  const key = `clubcore.pos-mode.${club.slug}`;
  const [saved, setSaved] = useState<{ key: string; home: string | null }>(
    () => {
      try {
        return { key, home: localStorage.getItem(key) };
      } catch {
        return { key, home: null };
      }
    },
  );
  const home =
    saved.key === key && saved.home && isPosWorkspace(saved.home)
      ? saved.home
      : null;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const api = useApi();
  const eligible = isPosWorkspace(pathname);
  useEffect(() => {
    if (!eligible) return;
    let deviceKey: string;
    try {
      deviceKey =
        localStorage.getItem("clubcore.pos-device") ?? crypto.randomUUID();
      localStorage.setItem("clubcore.pos-device", deviceKey);
    } catch {
      return;
    }
    const report = () => {
      void api.pos
        .reportTerminal({
          device_key: deviceKey,
          name: `${pathname.startsWith("/pos/kitchen") ? "Kitchen" : "Register"} · ${navigator.platform || "Browser"}`.slice(
            0,
            100,
          ),
          status: home ? "locked" : document.hidden ? "online" : "in_use",
        })
        .catch(() => undefined);
    };
    report();
    const timer = window.setInterval(report, 30000);
    document.addEventListener("visibilitychange", report);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", report);
    };
  }, [eligible, pathname, home, api, club.slug]);
  useEffect(() => {
    if (home && !isPosWorkspace(pathname))
      void navigate(home, { replace: true });
  }, [home, pathname, navigate]);
  const setHome = (value: string | null) => {
    // A failed storage write still locks this session; never imply persistence when unavailable.
    try {
      if (value) localStorage.setItem(key, value);
      else localStorage.removeItem(key);
    } catch {
      /* restricted storage */
    }
    setSaved({ key, home: value });
  };
  return {
    locked: Boolean(home),
    eligible,
    lock: () =>
      setHome(
        pathname.startsWith("/pos/kitchen") ? "/pos/kitchen" : "/pos/checks",
      ),
    unlock: () => setHome(null),
  };
}

export function PosModeControl({
  locked,
  lock,
  unlock,
}: {
  locked: boolean;
  lock: () => void;
  unlock: () => void;
}) {
  const t = useT();
  const { club } = useClub();
  const me = useMe();
  const describe = useErrorMessage();
  const [method, setMethod] = useState<"qr" | "password">("qr");
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const close = () => {
    if (!busy) {
      setOpen(false);
      setPassword("");
      setError(null);
    }
  };
  return (
    <>
      <Button
        onClick={() => {
          if (locked) {setEmail(me.user.email);setMethod("qr");setOpen(true);}
          else lock();
        }}
      >
        {t(locked ? "pos.mode.unlock" : "pos.mode.lock")}
      </Button>
      <Modal
        open={open}
        onOpenChange={(value) => {
          if (!value) close();
        }}
        title={t("pos.mode.unlock")}
        description={t("pos.mode.managerHint")}
      >
        <div className="cc-pos-exit-methods" aria-label={t("pos.exit.method")}>
          <Button disabled={busy} aria-pressed={method==="qr"} onClick={()=>{setMethod("qr");setError(null);setPassword("");}}>{t("pos.exit.scanQr")}</Button>
          <Button disabled={busy} aria-pressed={method==="password"} onClick={()=>{setMethod("password");setError(null);}}>{t("pos.exit.usePassword")}</Button>
        </div>
        {method==="qr" ? <><QrExit key={club.slug} onApproved={()=>{unlock();setOpen(false);}}/><div className="cc-form-actions"><Button onClick={close}>{t("common.cancel")}</Button></div></> : <form
          onSubmit={(event) => {
            event.preventDefault();
            setBusy(true);
            setError(null);
            const manager = createClubCoreClient({
              baseUrl: import.meta.env.VITE_API_BASE_URL ?? "/api/v1",
              tokenStore: new MemoryTokenStore(),
              getClub: () => club.slug,
            });
            void (async () => {
              try {
                await manager.auth.login({
                  email,
                  password,
                  device: "POS mode unlock",
                });
                const current = await manager.core.currentClub();
                if (!current.permissions.includes("pos.manage"))
                  throw new ApiError("forbidden", {
                    status: 403,
                    code: "pos_manager_required",
                    message: t("pos.mode.managerRequired"),
                  });
                unlock();
                setOpen(false);
              } catch (cause) {
                setError(cause);
              } finally {
                setPassword("");
                await manager.auth.logout().catch(() => undefined);
                setBusy(false);
              }
            })();
          }}
        >
          <p className="cc-muted">{t("pos.exit.accountHint")}</p>
          <TextField
            label={t("auth.email")}
            type="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
          <TextField
            label={t("auth.password")}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          {error ? <p className="cc-alert" role="alert">{describe(error)}</p> : null}
          <div className="cc-form-actions">
            <Button onClick={close} disabled={busy}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={busy || !email || !password}>
              {t("pos.mode.unlock")}
            </Button>
          </div>
        </form>}
      </Modal>
    </>
  );
}
